/**
 * Human-readable labels for Hangfire jobs.
 *
 * Most jobs are one class per concern (e.g. `GeniusClubsHarvestJob`), so the
 * class name alone is descriptive. The Presto harvest/sync jobs are different:
 * a SINGLE class dispatches every target via an enum argument
 * (`PrestoHarvestJob.RunAsync(target)`), so Hangfire — which names a job by its
 * class + method and ignores arguments — shows every target as an identical
 * "PrestoHarvestJob". This decodes the enum argument back into the target name
 * so the worker/recent-jobs panels can tell "Event Team Stats" from
 * "Event Rosters".
 *
 * The target is already present in the `invocationdata` jsonb the Admin fetches:
 *   Arguments      = "[null,\"8\",null]"   (JSON string; the enum serialized as
 *                                            its numeric ordinal)
 *   ParameterTypes = "[\"...PerformContext...\",\"...PrestoHarvestTarget...\",
 *                       \"...CancellationToken...\"]"
 * We map the ordinal back to a name using ParameterTypes to pick the right enum.
 */

// ⚠️ These arrays are POSITIONAL — index = enum ordinal — and MUST stay in sync
// with the C# enums:
//   src/RevoDataPlatform.Collector/Jobs/PrestoHarvestTarget.cs
//   src/RevoDataPlatform.Collector/Jobs/PrestoSyncTarget.cs
// Both declare implicit 0-based ordinals, and Hangfire serializes the argument
// as that numeric value. Treat as APPEND-ONLY: never reorder or remove an entry
// (doing so would mislabel historical job rows). Adding new targets at the end
// is safe; an unknown ordinal simply falls back to the bare class name.
const ENUM_TARGETS: Record<string, readonly string[]> = {
  PrestoHarvestTarget: [
    'Reference',
    'RpiData',
    'SeasonTournaments',
    'SeasonTeams',
    'SeasonEvents',
    'TeamRosters',
    'TeamStats',
    'EventRosters',
    'EventTeamStats',
    'EventPlayerStats',
    'CareerStats',
  ],
  PrestoSyncTarget: [
    'Sports',
    'Organizations',
    'Seasons',
    'Tournaments',
    'RpiData',
    'Teams',
    'Players',
    'Events',
    'EventRosters',
    'EventTeamStats',
    'EventPlayerStats',
    'CareerStats',
    'TeamSeasonStats',
    'TeamRecords',
    'TeamPlayerSeasonStats',
  ],
};

/** Shape of Hangfire's invocationdata jsonb (we read only what we need). */
interface InvocationData {
  Type?: string;
  Method?: string;
  // Both are JSON-encoded STRINGS inside the jsonb, not arrays.
  Arguments?: string;
  ParameterTypes?: string;
}

function asInvocation(raw: unknown): InvocationData | null {
  if (raw == null) return null;
  if (typeof raw === 'string') {
    try {
      return JSON.parse(raw) as InvocationData;
    } catch {
      return null;
    }
  }
  if (typeof raw === 'object') return raw as InvocationData;
  return null;
}

/**
 * Simple type name from an assembly-qualified .NET type string:
 * "RevoDataPlatform.Collector.Jobs.PrestoHarvestJob, Asm" -> "PrestoHarvestJob".
 */
function shortTypeName(qualified: string): string {
  const beforeComma = qualified.split(',')[0];
  return beforeComma.split('.').pop()?.trim() || beforeComma;
}

/** "EventTeamStats" -> "Event Team Stats"; "RpiData" -> "Rpi Data". */
export function humanizeTarget(name: string): string {
  return name
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .replace(/([A-Z]+)([A-Z][a-z])/g, '$1 $2')
    .trim();
}

/**
 * Decode the dispatched target (if any) from an invocationdata's Arguments,
 * keyed by the matching enum in ParameterTypes. Returns the humanized target
 * name (e.g. "Event Team Stats") or null when the job has no enum target arg.
 */
function decodeTarget(inv: InvocationData): string | null {
  if (!inv.Arguments || !inv.ParameterTypes) return null;
  let args: unknown[];
  let paramTypes: unknown[];
  try {
    args = JSON.parse(inv.Arguments) as unknown[];
    paramTypes = JSON.parse(inv.ParameterTypes) as unknown[];
  } catch {
    return null;
  }
  if (!Array.isArray(args) || !Array.isArray(paramTypes)) return null;

  for (let i = 0; i < paramTypes.length; i++) {
    const pt = paramTypes[i];
    if (typeof pt !== 'string') continue;
    const targets = ENUM_TARGETS[shortTypeName(pt)];
    if (!targets) continue;
    const rawArg = args[i];
    // Hangfire serializes the enum as its numeric ordinal, stored as a string
    // (e.g. "8"); tolerate a plain number too.
    const ordinal = typeof rawArg === 'string' ? Number(rawArg) : rawArg;
    if (typeof ordinal !== 'number' || !Number.isInteger(ordinal)) continue;
    const target = targets[ordinal];
    if (target) return humanizeTarget(target);
  }
  return null;
}

/**
 * Descriptive label for a Hangfire job from its invocationdata.
 *  - enum-dispatched jobs  -> "PrestoHarvestJob — Event Team Stats"
 *  - everything else        -> "GeniusClubsHarvestJob"
 * Returns null when the row has no resolvable Type, so callers can apply their
 * own fallback ("Unknown job", "—", a truncated raw blob, etc.).
 */
export function describeJob(raw: unknown): string | null {
  const inv = asInvocation(raw);
  if (!inv?.Type) return null;
  // Deliberately no ".RunAsync" suffix: every job class has exactly one entry
  // method, so the method name is pure noise (the old "PrestoSyncJob.RunAsync"
  // labels were the original complaint).
  const className = shortTypeName(inv.Type);
  const target = decodeTarget(inv);
  return target ? `${className} — ${target}` : className;
}
